/**
 * Light / dark / system theme (zustand). The choice is kept in localStorage;
 * index.html applies it before first paint so there's no light flash.
 */
import { create } from 'zustand';

export type ThemeChoice = 'light' | 'dark' | 'system';
const KEY = 'talky-admin-theme';

function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

const media = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-color-scheme: dark)') : null;

export function applyTheme(choice: ThemeChoice) {
  const dark = choice === 'dark' || (choice === 'system' && Boolean(media?.matches));
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}

interface ThemeState {
  choice: ThemeChoice;
  setChoice: (c: ThemeChoice) => void;
}

export const useTheme = create<ThemeState>((set) => ({
  choice: readChoice(),
  setChoice: (choice) => {
    try {
      if (choice === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, choice);
    } catch {
      // private mode: the choice still applies for this visit
    }
    applyTheme(choice);
    set({ choice });
  },
}));

// Follow the OS setting live while on "system"
media?.addEventListener?.('change', () => {
  if (useTheme.getState().choice === 'system') applyTheme('system');
});
